/**
 * ATRIBUIR LOS MOVIMIENTOS DE UNA CUENTA COMPARTIDA (BAN-T3, Ares #28212). Sin DOM.
 *
 * Lee los destinos y la lista del servidor (tolera camelCase y PascalCase) y lleva lo que el administrador atribuyo en
 * esta sesion. No decide nada del banco: solo ordena y cuenta. Contrato: P-CONTRATO-ATRIBUCION-BANCO-BAN-T3.
 *
 * @module application/conciliacion/atribucionBanco
 */

const de = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};
const lista = (v) => (Array.isArray(v) ? v : []);

/** {multiNegocio, destinos[{destino, nombre, esDueno}]}. Sin multiNegocio explicito = false (no se muestra). */
export function normalizarDestinos(data) {
  const d = data || {};
  const destinos = lista(de(d, 'destinos', 'Destinos')).map((x) => ({
    destino: String(de(x, 'destino', 'Destino') ?? ''),
    nombre: String(de(x, 'nombre', 'Nombre') ?? ''),
    esDueno: de(x, 'esDueno', 'EsDueno') === true
  })).filter((x) => x.destino);
  // Condicion dura de BAN-T3: un negocio solo NUNCA ve el selector. Hace falta el true del servidor y 2+ destinos.
  const multiNegocio = de(d, 'multiNegocio', 'MultiNegocio') === true && destinos.length >= 2;
  return { multiNegocio, destinos: multiNegocio ? destinos : [] };
}

/** Un movimiento de la lista (BankMovementRecord). */
export function normalizarMovimiento(x) {
  return {
    id: String(de(x, 'id', 'Id') ?? ''),
    fecha: String(de(x, 'fecha', 'Fecha') ?? '').slice(0, 10),
    descripcion: String(de(x, 'descripcion', 'Descripcion') ?? ''),
    valor: Number(de(x, 'valor', 'Valor')) || 0,
    sugerida: de(x, 'atribucionSugerida', 'AtribucionSugerida'),
    motivo: de(x, 'sugerenciaMotivo', 'SugerenciaMotivo')
  };
}

/** Los movimientos del mes 'yyyy-MM', en orden de fecha (la lista del servidor no filtra por rango). */
export function movimientosDelMes(items, mes) {
  const m = String(mes || '');
  return lista(items).map(normalizarMovimiento).filter((x) => x.id && (!m || x.fecha.startsWith(m + '-')))
    .sort((a, b) => a.fecha.localeCompare(b.fecha) || a.id.localeCompare(b.id));
}

/** Los meses con movimientos, del mas reciente al mas viejo (para elegir sin adivinar). */
export function mesesConMovimientos(items) {
  const s = new Set(lista(items).map(normalizarMovimiento).map((x) => x.fecha.slice(0, 7)).filter((x) => /^\d{4}-\d{2}$/.test(x)));
  return [...s].sort().reverse();
}

/** La sugerencia solo cuenta si es uno de los destinos de la cuenta. */
export function sugerenciaValida(mov, destinos) {
  return mov?.sugerida && lista(destinos).some((d) => d.destino === mov.sugerida) ? mov.sugerida : null;
}

/**
 * El cuerpo de movimientos para el POST: lo marcado con su destino. hechos = {id: destino} de la sesion: lo que ya
 * tiene ese mismo destino no se vuelve a mandar (el servidor es idempotente, pero no gastamos el viaje).
 */
export function cuerpoAtribucion(ids, destino, hechos = {}) {
  return lista(ids).filter((id) => id && hechos[id] !== destino).map((id) => ({ bankMovementId: String(id), destino }));
}

/** Cuentas: cuantos, cuantos ya atribuidos en la sesion y por destino. */
export function resumenAtribucion(movs, hechos = {}) {
  const r = { total: 0, pendientes: 0, atribuidos: 0, porDestino: {} };
  for (const m of lista(movs)) {
    r.total++;
    const h = hechos[m.id];
    if (h) { r.atribuidos++; r.porDestino[h] = (r.porDestino[h] || 0) + 1; } else r.pendientes++;
  }
  return r;
}
